import { useEffect, useRef } from 'react'
import styles from './Spinner.module.css'
import { cn } from '../../../utils'

const TAU = Math.PI * 2

function mulberry32(a: number) {
  return function () {
    a |= 0
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

function makeNoise(seed: number) {
  const rng = mulberry32(seed)
  const grid = Array.from({ length: 256 }, () => rng() * 2 - 1)
  return (x: number) => {
    const i = Math.floor(x)
    const f = x - i
    const a = grid[i & 255]
    const b = grid[(i + 1) & 255]
    const u = f * f * (3 - 2 * f)
    return a * (1 - u) + b * u
  }
}

const ns = [makeNoise(21), makeNoise(22), makeNoise(23)]

interface SpinnerProps {
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

export function Spinner({ size = 'md', className }: SpinnerProps) {
  const filterId = useRef(`goo-${Math.random().toString(36).slice(2)}`).current
  const c1Ref = useRef<SVGCircleElement>(null)
  const c2Ref = useRef<SVGCircleElement>(null)
  const c3Ref = useRef<SVGCircleElement>(null)
  const rafRef = useRef<number>(0)

  useEffect(() => {
    const circles = [c1Ref.current, c2Ref.current, c3Ref.current]

    function tick(now: number) {
      const t = now / 1000
      for (let i = 0; i < 3; i++) {
        const c = circles[i]
        if (!c) continue
        const a = t * 0.9 + (i * TAU) / 3 + ns[i](t * 0.8) * 0.4
        const r = 42 + ns[i](t * 1.2 + 3) * 8
        c.setAttribute('cx', String(100 + Math.cos(a) * r))
        c.setAttribute('cy', String(100 + Math.sin(a) * r))
        c.setAttribute('r', String(16 + ns[i](t + i) * 4))
      }
      rafRef.current = requestAnimationFrame(tick)
    }

    rafRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafRef.current)
  }, [])

  const px = size === 'sm' ? 20 : size === 'md' ? 32 : 96

  return (
    <svg
      className={cn(styles.organicSpinner, className)}
      viewBox="0 0 200 200"
      width={px}
      height={px}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <filter id={filterId}>
          <feGaussianBlur in="SourceGraphic" stdDeviation="6" result="blur" />
          <feColorMatrix
            in="blur"
            values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 22 -11"
          />
        </filter>
      </defs>
      <g filter={`url(#${filterId})`}>
        <circle cx="100" cy="100" r="22" className={styles.blob} />
        <circle ref={c1Ref} r="18" className={styles.blob} />
        <circle ref={c2Ref} r="18" className={styles.blob} />
        <circle ref={c3Ref} r="18" className={styles.blob} />
      </g>
    </svg>
  )
}

interface LoadingStateProps {
  message?: string
}

export function LoadingState({ message = 'Loading...' }: LoadingStateProps) {
  return (
    <div className={styles.loadingState}>
      <Spinner size="lg" />
      <span className={styles.message}>{message}</span>
    </div>
  )
}
